/**
 * @module airline.service
 */
import { DialVerseApi } from '../app/api'
import AppConfig from '../config'

const AirlineService = DialVerseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAirlines: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/airlines/list'),
        method: 'GET',
      }),
    }),
    getAirline: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}`),
        method: 'GET',
      }),
    }),
    createAirline: builder.mutation({
      query: (airline) => ({
        url: AppConfig.getUrl('/airlines'),
        method: 'POST',
        body: airline,
      }),
    }),
    updateAirline: builder.mutation({
      query: ({ airlineId, ...updates }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}`),
        method: 'PUT',
        body: updates,
      }),
    }),
    deleteAirline: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}`),
        method: 'DELETE',
      }),
    }),
    importAirlines: builder.mutation({
      query: (airlinesData) => ({
        url: AppConfig.getUrl('/airlines/import'),
        method: 'POST',
        body: airlinesData,
      }),
    }),

    // Additional operation routes
    updateStatus: builder.mutation({
      query: ({ airlineId, status }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/status`),
        method: 'PUT',
        body: { status },
      }),
    }),
    addDestination: builder.mutation({
      query: ({ airlineId, destination }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/destinations`),
        method: 'POST',
        body: { destination },
      }),
    }),
    removeDestination: builder.mutation({
      query: ({ airlineId, destination }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/destinations/${destination}`),
        method: 'DELETE',
      }),
    }),
    addHubAirport: builder.mutation({
      query: ({ airlineId, hubAirport }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/hubs`),
        method: 'POST',
        body: { hubAirport },
      }),
    }),
    removeHubAirport: builder.mutation({
      query: ({ airlineId, hubAirport }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/hubs/${hubAirport}`),
        method: 'DELETE',
      }),
    }),
    updateFleetSize: builder.mutation({
      query: ({ airlineId, fleetSize }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/fleet-size`),
        method: 'PUT',
        body: { fleetSize },
      }),
    }),
    updateContactInfo: builder.mutation({
      query: ({ airlineId, contactInfo }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/contact-info`),
        method: 'PUT',
        body: { contactInfo },
      }),
    }),
    updateSocialMedia: builder.mutation({
      query: ({ airlineId, socialMedia }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/social-media`),
        method: 'PUT',
        body: { socialMedia },
      }),
    }),
    updateRegulatoryInfo: builder.mutation({
      query: ({ airlineId, regulatoryInfo }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/regulatory-info`),
        method: 'PUT',
        body: { regulatoryInfo },
      }),
    }),
    updateFinancialInfo: builder.mutation({
      query: ({ airlineId, financialInfo }) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/financial-info`),
        method: 'PUT',
        body: { financialInfo },
      }),
    }),

    // Analytics routes
    getAnnualGrowth: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/analytics/growth`),
        method: 'GET',
      }),
    }),
    getCertificateStatus: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/analytics/certificate-status`),
        method: 'GET',
      }),
    }),
    getHubCount: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/analytics/hub-count`),
        method: 'GET',
      }),
    }),
    getDestinationCount: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/airlines/${airlineId}/analytics/destination-count`),
        method: 'GET',
      }),
    }),

    // Search routes
    findByIataCode: builder.mutation({
      query: (iataCode) => ({
        url: AppConfig.getUrl(`/airlines/search/iata/${iataCode}`),
        method: 'GET',
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetAirlinesMutation,
  useGetAirlineMutation,
  useCreateAirlineMutation,
  useUpdateAirlineMutation,
  useDeleteAirlineMutation,
  useImportAirlinesMutation,
  useUpdateStatusMutation,
  useAddDestinationMutation,
  useRemoveDestinationMutation,
  useAddHubAirportMutation,
  useRemoveHubAirportMutation,
  useUpdateFleetSizeMutation,
  useUpdateContactInfoMutation,
  useUpdateSocialMediaMutation,
  useUpdateRegulatoryInfoMutation,
  useUpdateFinancialInfoMutation,
  useGetAnnualGrowthMutation,
  useGetCertificateStatusMutation,
  useGetHubCountMutation,
  useGetDestinationCountMutation,
  useFindByIataCodeMutation,
} = AirlineService
